import React, { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import withUrlParam from '../../hocs/withUrlParam';
import Breadcrumbs from '../../components/Breadcrumbs/breadcrumbs';
import CopyButton from '../../components/CopyButton';
import AsyncSection from '../Gateways/AsyncSection';
import useRetailerBatteries from '../../hooks/batteries/useRetailerBatteries';
import useBatteryDelivered from '../../hooks/batteries/useBatteryDelivered';
import useMemberStatus from '../../hooks/batteries/useMemberStatus';
import HealthPill from './HealthPill';
import BasicProperties from './panels/BasicProperties';
import PrequalifiedCapacity from './panels/PrequalifiedCapacity';
import OperatingStatus, { latestState } from './panels/OperatingStatus';
import DevicesTable from './panels/DevicesTable';
import DeliveryReport from './panels/DeliveryReport';
import { StatTile } from './panels/primitives';
import { summarizeDelivery } from './lib/deliverySummaryV1';
import { fmtKw } from './lib/deliveredWire';
import { fmtStamp } from './lib/chartTheme';
import { pastRange } from './lib/recordWindow';

// Below this share of committed periods backed, the Backed tile warns. Not a contractual
// threshold; set where a plain figure would otherwise read as fine.
export const BACKED_WARN = 0.9;

const NO_MEMBERS = [];

const BatteryPage = ({ paramValue: batteryId }) => {
    const location = useLocation();
    // The list passes the battery row through router state so the hero renders at once; on a
    // direct load there is no state, so the list is fetched and the battery looked up in it.
    const fromState = location.state?.battery?.id === batteryId ? location.state.battery : null;
    const { batteries, isReady, error } = useRetailerBatteries();
    const battery = fromState ?? batteries.find(b => b.id === batteryId) ?? null;

    // Seven days of the settled record feed the hero tiles, the basic properties (product,
    // period) and the operating status. Memoised per battery: `pastRange` reads the clock,
    // and a fresh `to` on every render would refetch on every render.
    const weekRange = useMemo(() => pastRange(7), [batteryId]); // eslint-disable-line react-hooks/exhaustive-deps
    const week = useBatteryDelivered(batteryId, weekRange);
    const weekStats = useMemo(() => summarizeDelivery(week.reports), [week.reports]);
    const latest = latestState(week.reports);

    const memberIds = useMemo(() => (battery?.members ?? NO_MEMBERS).map(m => m.deviceId), [battery]);
    const memberStatus = useMemberStatus(memberIds);
    const reporting = memberIds.filter(id => memberStatus.healthByDevice.get(id)?.status).length;

    const crumbs = [
        { name: 'Virtual batteries', to: '/batteries' },
        { name: battery?.name ?? 'Battery', to: `/batteries/${batteryId}` },
    ];

    return (
        <div className="vb-page">
            <Breadcrumbs crumbs={crumbs} />
            <AsyncSection
                isReady={isReady || fromState !== null}
                error={error && !fromState}
                isEmpty={battery === null}
                errorText="Could not load this virtual battery."
                emptyText={`No virtual battery with id ${batteryId} is registered to your account.`}
            >
                {battery ? (
                    <>
                        <div className="gateway-page__hero vb-page__hero">
                            <div className="gateway-page__hero-main">
                                <div className="gateway-page__hero-icon">
                                    <img src="/assets/icons/battery.svg" alt="" className="vb-page__hero-img" />
                                </div>
                                <div>
                                    <div className="vb-page__title-row">
                                        <h1 className="gateway-page__name">{battery.name}</h1>
                                        <HealthPill status={battery.health} />
                                    </div>
                                    <div className="gateway-page__sub">
                                        <span className="vb-page__id">
                                            {battery.id}
                                            <CopyButton value={battery.id} />
                                        </span>
                                        {battery.priceArea ? <span>{battery.priceArea}</span> : null}
                                        <span>
                                            {battery.members.length} {battery.members.length === 1 ? 'device' : 'devices'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                            <div className="vb-page__tiles">
                                <StatTile
                                    label="Committed now"
                                    value={latest?.markets ? fmtKw(latest.markets.committedPower) : week.isReady ? '—' : '…'}
                                    unit={latest?.markets ? 'kW' : undefined}
                                    hint={
                                        latest ? `settled ${fmtStamp(Date.parse(latest.end))}` : week.isReady ? 'no settled report in 7 d' : undefined
                                    }
                                    info="The commitment on the newest settled delivery report."
                                />
                                <StatTile
                                    label="Backed 7d"
                                    value={weekStats?.backedRate != null ? `${(weekStats.backedRate * 100).toFixed(0)}%` : week.isReady ? '—' : '…'}
                                    tone={weekStats?.backedRate != null && weekStats.backedRate < BACKED_WARN ? 'warn' : undefined}
                                    hint={
                                        weekStats?.backedRate != null
                                            ? `${weekStats.backedCount} of ${weekStats.committedCount} committed periods`
                                            : week.isReady
                                            ? 'nothing committed'
                                            : undefined
                                    }
                                    info="Share of committed periods in the last 7 days where the minimum available power reached the commitment."
                                />
                                <StatTile
                                    label="Devices reporting"
                                    // A plain count, not a health verdict: how many devices filed a
                                    // health report at all — real even while the verdicts read TBD.
                                    value={memberStatus.isReady ? `${reporting} / ${memberIds.length}` : '…'}
                                    hint={
                                        memberStatus.isReady && reporting < memberIds.length
                                            ? `${memberIds.length - reporting} without a health report`
                                            : undefined
                                    }
                                />
                            </div>
                        </div>

                        <BasicProperties battery={battery} reports={week.reports} reportsReady={week.isReady} />
                        <PrequalifiedCapacity battery={battery} />
                        <OperatingStatus battery={battery} reports={week.reports} reportsReady={week.isReady} memberStatus={memberStatus} />
                        <DeliveryReport batteryId={batteryId} />
                        <DevicesTable battery={battery} memberStatus={memberStatus} />
                    </>
                ) : null}
            </AsyncSection>
        </div>
    );
};

export default withUrlParam('id')(BatteryPage);
